import { gql } from "@apollo/client";
import { LinkedInCompanyCellInfo_LinkedInCompanyFragment } from "../../../__generated__/graphql";
import CiroTooltipContainer from "../CiroTooltipContainer";
import classNames from "classnames";
import CiroLink from "../CiroLink";
import { getLinkedinCompanyUrlFromId } from "../../../utils/linkedinUtils";

export const LinkedInCompanyCellInfo_LinkedInCompany = gql`
  fragment LinkedInCompanyCellInfo_LinkedInCompany on LinkedInCompany {
    id
    linkedin_id
    profile {
      profile_pic_url
      follower_count
      industry
      hq {
        city
        state
        country
      }
      company_size
      name
    }
  }
`;

const createLocationString = ({
  city,
  state,
  country,
}: {
  city: string | null | undefined;
  state: string | null | undefined;
  country: string | null | undefined;
}) => {
  if (!city && !state && !country) {
    return null;
  }

  return [city, state, country !== "US" ? country : null]
    .filter(Boolean)
    .join(", ");
};

const createCompanySizeString = ({
  min,
  max,
}: {
  min: number | null | undefined;
  max: number | null | undefined;
}) => {
  if (!min && !max) {
    return null;
  }

  if (min === max) {
    return `${min} employees`;
  }

  if (!max) {
    return `${min}+ employees`;
  }

  return `${min} - ${max} employees`;
};

const LinkedInCompanyCellInfo = ({
  linkedInCompany,
  companyName,
}: {
  linkedInCompany:
    | LinkedInCompanyCellInfo_LinkedInCompanyFragment
    | null
    | undefined;
  companyName: string;
}) => {
  if (!linkedInCompany?.profile) {
    return <div>{companyName}</div>;
  }

  const { profile } = linkedInCompany;

  const subheaderData = [
    profile.industry,
    profile.hq
      ? createLocationString({
          city: profile.hq.city,
          state: profile.hq.state,
          country: profile.hq.country,
        })
      : null,
    profile.follower_count ? `${profile.follower_count} followers` : null,
    profile.company_size
      ? createCompanySizeString({
          min: profile.company_size[0],
          max: profile.company_size[1],
        })
      : null,
  ].filter(Boolean);

  const linkedinCompanyUrl = getLinkedinCompanyUrlFromId(
    linkedInCompany.linkedin_id,
  );

  return (
    <CiroTooltipContainer
      tooltip={
        <div className={classNames("flex", "gap-2", "items-center")}>
          {/* TODO: Add back profile pictures once we are able to refresh them on a
          consistent basis (CIR-3560)
          {profile.profile_pic_url && (
            <img
              src={profile.profile_pic_url}
              alt={profile.name}
              className={classNames("w-10", "h-10", "rounded-full")}
            />
          )} */}
          <div>
            <div className={classNames("text-sm")}>{profile.name}</div>

            <div className={classNames("text-xs", "text-gray-500")}>
              {subheaderData.join(" • ")}
            </div>
          </div>
        </div>
      }
    >
      {linkedinCompanyUrl ? (
        <CiroLink href={linkedinCompanyUrl}>{profile.name}</CiroLink>
      ) : (
        <div>{profile.name}</div>
      )}
    </CiroTooltipContainer>
  );
};

export default LinkedInCompanyCellInfo;
